// Eseménynapló (activity-log, BELSŐ nézet) — a KÖZÖS DataTable-re épül: szerver-oldali (BFF) kereső (300 ms
// debounce; művelet/entitás) + NÉZET-preset (kategória) + szereplő- és dátum-szűrő + darabszám-meta, végtelen
// görgetés. A lapozás KEYSET-CURSOR (a `/v1/admin/audit-events` cursor-lapozású) → a `useInfiniteQuery` a
// `nextCursor`-t viszi `pageParam`-ként; szűrő-váltáskor a queryKey-csere az első lapra resetel.
//
// OLVASHATÓSÁG (nem kulcs-értékek): a művelet EMBER-OLVASHATÓ címke (natív `{type}.{verb}` → „Fiók módosítása";
// legacy/auth kulcsok → kurált i18n; ismeretlen → humanizált fallback). Az érintett entitás lokalizált típus +
// rövid ref. A „mi változott" mezőszintű régi→új diff (a szerver REDAKÁLT `changes` mezőjéből — a nyers
// before/after SOHA nem jön ki: PII-fegyelem), kinyíló panelben (mint a legacy „differences").
//
// A feed egy SZŰRT nézet a teljes audit-trailre: a NÉZET-preset a kategóriát választja (alap: data+admin+
// auth_security; a session/security/system ZAJ alapból rejtve, de előhívható). Page-gate: `activity_log.view`.
import { type ActivityEventSchema } from '@atlas/contracts';
import { ChevronRightIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DomainBadge } from '@/components/shared/domain-badge';
import { entityIcon } from '@/features/activity-log/entity-icons';
import { EmptyState } from '@/components/shared/empty-state';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListExportButton } from '@/components/shared/list-export-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { PersonCell } from '@/components/shared/person-cell';
import { Badge } from '@/components/ui/badge';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { activityLogKeys } from '@/features/activity-log/api';
import { diffValueLabel } from '@/features/activity-log/value-label';
import { useListExport } from '@/hooks/use-list-export';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { formatDateTime, useTimeZone } from '@/lib/format-datetime';
import { formatName } from '@/lib/format-name';
import { DEFAULT_APP, useAdminApps } from '@/lib/use-admin-apps';
import { usePermissions } from '@/lib/use-permissions';
import { cn } from '@/lib/utils';
import { isForbiddenStatus } from '@/lib/error-status';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const activityLogSearchSchema = z.object({
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  view: z.string().optional(),
  app: z.string().optional(),
});

export const Route = createFileRoute('/activity-log')({
  validateSearch: (search) => activityLogSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: ActivityLogPage,
});

type ActivityEvent = z.infer<typeof ActivityEventSchema>;

const PAGE_SIZE = 100;

// A natív {type}.{verb} taxonómia igéi → kompozíciós címke ("Fiók" + "módosítása").
const VERBS = new Set(['create', 'update', 'delete', 'archive', 'recalculate']);

// NÉZET-preset → a BFF `category` paramétere (undefined → a szerver default feed-nézete: data+admin+auth_security).
const VIEW_PRESETS: Record<string, string | undefined> = {
  default: undefined,
  all: 'data,admin,auth_security,auth_session,security,system',
  data: 'data',
  admin: 'admin',
  auth: 'auth_session,auth_security',
  security: 'security',
  system: 'system',
};

/**
 * A ref-ből kiolvasott entitás-TÍPUS — a sor alatti kis badge-hez („fiók", "találkozó", "fájl").
 *
 * MIÉRT KELL: a napló-sorból eddig nem derült ki, MILYEN entitáson történt a művelet — egy „MORGENS Design Kft."
 * entitás-név mellett nem tudtad, fájl-letöltés volt-e, üzenet-melléklet vagy fiók-módosítás. A típus-chip a
 * scannelhető jel (így csinálja a Stripe/GitHub/CloudTrail is: erőforrás-TÍPUS + azonosító).
 *
 * CSAK a natív `{namespace}/{type}/{id}` (és a `files/{ns}/{id}`) alakra ad típust. A legacy/admin ref-alakok
 * (`legacy:…`, `typed:…`, `import:…`, `role:…`, `app:…`, `identity:…`) SZÁNDÉKOSAN badge NÉLKÜL maradnak: ott a
 * művelet-címke hordozza a jelentést, és egy „legacy-crm" chip csak zaj lenne. A címke a MÁR LÉTEZŐ
 * `activityLog.entity.*` szótárból jön (a falak ezt hu+en oldalon kikényszerítik) — ha nincs kulcs, nincs badge
 * (sosem írunk ki nyers slugot).
 */
function refEntityType(ref: string | null): string | null {
  if (!ref || ref.includes(':')) return null;
  if (ref.startsWith('files/')) return 'file'; // `files/{ns}/{id}` → a meglévő `file` címke
  const parts = ref.split('/');
  const type = parts.length >= 3 ? (parts[1] ?? null) : null;
  return type ? (TYPE_ALIAS[type] ?? type) : null;
}

/**
 * Néhány resource-típus TECHNIKAI név, amit a felhasználónak más néven kell látnia. Az olvasási visszajelzés
 * a legjobb példa: a sor a `message-reads` táblára hivatkozik, de a felhasználót az érdekli, MELYIK ÜZENETRŐL
 * van szó — a név ezért az üzenet tárgya (Odin-oldali feloldás), a chip pedig „üzenet". („Rendszerüzenet"
 * nincs a modellben: a típus `internal`/`external`, és azt a levelező-modul mutatja, nem a napló.)
 */
const TYPE_ALIAS: Record<string, string> = { 'message-reads': 'message' };

/** Nyers művelet-kulcs → humanizált fallback (pont/aláhúzás → szóköz). */
function humanize(action: string): string {
  return action.replace(/[._]+/g, ' ').trim();
}

export function ActivityLogPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('activity_log.view');

  // KÖZPONTI (multi-app) konzol: app-szűrő. DEFAULT az Atlas (a saját app); „Minden app" (`''`) → kereszt-app
  // feed. Csak TÖBB regisztrált appnál jelenik meg a szűrő (különben az oldal a mostani, egy-appos nézet).
  const { apps, showSelector } = useAdminApps();

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    app: DEFAULT_APP,
    from: '',
    to: '',
    view: 'default',
  });
  const q = urlFilters.q;
  const appFilter = urlFilters.app;
  const from = urlFilters.from;
  const to = urlFilters.to;
  const view = urlFilters.view;
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);

  // Kereső-debounce (300 ms) — a dátum/nézet-mezők azonnal frissítenek (nincs gépelés-zaj). Az EGYSÉGES kereső
  // a szerveren illeszt: művelet + entitás (resource_ref) + tenant (ügyfél/fiók) + szereplő (név/e-mail) — így
  // nincs külön szereplő-szűrő.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: activity_log.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  const category = VIEW_PRESETS[view];
  const filters = { q, from, to, category: category ?? '' };
  // Az app-szűrő a query-kulcs része → app-váltáskor újratölt (első lapra reset).
  const listKey = [...activityLogKeys.list(filters), appFilter];

  // KEYSET-cursor lapozás: a `pageParam` a cursor (első lap: undefined); a `nextCursor` viszi tovább.
  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.activityLog.query({
        query: {
          q: q || undefined,
          from: from || undefined,
          to: to || undefined,
          category,
          // Üres appFilter (`''` = „Minden app") → nincs `app` param → az Odin kereszt-app feedet ad.
          app: appFilter || undefined,
          limit: PAGE_SIZE,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextCursor ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.events : [])),
    [pages],
  );
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters =
    // a „mind" az egyetlen szűretlen állapot: az alapnézet és az alap app (Atlas) is szűkítés (Andi #95); az
    // app-választó csak több appnál látszik, egy appnál az app nem szűrő
    q !== '' || from !== '' || to !== '' || view !== 'all' || (showSelector && appFilter !== '');

  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Ember-olvasható művelet-címke: (1) natív {type}.{verb} → „Fiók módosítása"; (2) kurált i18n a legacy/auth
  // kulcsokhoz; (3) humanizált fallback. SOHA nem a nyers kulcs (a felhasználónak olvasható).
  const actionLabel = useCallback(
    (action: string): string => {
      const parts = action.split('.');
      if (parts.length === 2 && VERBS.has(parts[1]!)) {
        const entity = t(`activityLog.entity.${parts[0]}`, { defaultValue: humanize(parts[0]!) });
        const verb = t(`activityLog.verb.${parts[1]}`, { defaultValue: parts[1]! });
        return t('activityLog.actionPattern', { entity, verb, defaultValue: `${entity} ${verb}` });
      }
      const curated = t(`activityLog.actionLabel.${action.replace(/\./g, '_')}`, {
        defaultValue: '',
      });
      return curated || humanize(action);
    },
    [t],
  );

  // Érintett entitás: a `namespace/type/id` (natív) / `legacy/type/id` refből lokalizált TÍPUS + rövid azonosító.
  // Az „Érintett entitás" oszlop OLVASHATÓ 2-sora — SOSEM nyers ref/UUID (csak atlas-adat, ember-olvashatóan):
  //  • admin (RBAC): érintett SZEMÉLY (feloldott név) + szerepkör; ha csak szerepkör (role-upsert) → az.
  //  • natív (`ns/type/id`): ügyfél/fiók név (primary) + entitás-típus (al-sor); tenant híján a típus a primary.
  //  • legacy / egyéb: ügyfél/fiók + a tárgykör az action-doménből (a haszontalan `legacy:…/N` refet elrejtjük).
  /**
   * Entitás-TÍPUS chip a sor alatt (ikon + kisbetűs címke). Csak akkor jelenik meg, ha a ref natív alakú ÉS
   * van rá lokalizált címke — nyers slugot sosem írunk ki. A `Badge` `muted` variánsa a legcsendesebb tónus:
   * a chip másodlagos jel, nem versenyez a névvel.
   */
  const entityBadge = (ref: string | null): JSX.Element | null => {
    const type = refEntityType(ref);
    if (!type) return null;
    const label = t(`activityLog.entity.${type}`, { defaultValue: '' });
    if (!label) return null;
    const Icon = entityIcon(type);
    return (
      <Badge variant="muted" icon={<Icon aria-hidden="true" />} className="font-normal">
        {label.toLocaleLowerCase(i18n.language)}
      </Badge>
    );
  };

  const targetLines = (e: ActivityEvent): { primary: string; secondary?: string } | null => {
    const ref = e.resourceRef ?? '';
    // Személy-név locale-sorrendben (mint a szereplő) — ha van név-rész, `formatName`; egyébként displayName.
    const personName = (
      given: string | null,
      family: string | null,
      display: string | null,
    ): string | null =>
      given || family
        ? formatName({ givenName: given, familyName: family, displayName: display }, i18n.language)
        : display;

    // Admin (RBAC): érintett SZEMÉLY (locale-név) + szerepkör.
    if (e.category === 'admin' || ref.startsWith('app:')) {
      const roleKey = /role:([a-z0-9_-]+)/i.exec(ref)?.[1] ?? null;
      const roleText = roleKey
        ? t('activityLog.roleTarget', {
            role: t(`perm.role.${roleKey}`, { defaultValue: humanize(roleKey) }),
          })
        : null;
      const person = personName(e.targetGivenName, e.targetFamilyName, e.targetName);
      if (person) return roleText ? { primary: person, secondary: roleText } : { primary: person };
      return roleText ? { primary: roleText } : null;
    }
    // Legacy SZEMÉLY-alany (lang_attributes név-részek) → locale-sorrend.
    if (e.subjectGivenName || e.subjectFamilyName) {
      const person = personName(e.subjectGivenName, e.subjectFamilyName, e.subjectName)!;
      return e.subjectDetail
        ? { primary: person, secondary: e.subjectDetail }
        : { primary: person };
    }
    // Legacy KONKRÉT entitás-név (pl. „Melea…", „#TKT-…").
    if (e.subjectName) {
      return e.subjectDetail
        ? { primary: e.subjectName, secondary: e.subjectDetail }
        : { primary: e.subjectName };
    }
    // Natív: a tenant (ügyfél/fiók) a konkrét entitás. Ha NINCS konkrét név → em-dash (NEM generikus típus-címke:
    // az a művelet-címkével redundáns lenne, pl. „Üzenet megtekintése" + „Üzenet" — zaj).
    const tenant = e.clientName ?? e.accountName ?? null;
    return tenant ? { primary: tenant } : null;
  };

  // Időzóna-tudatos megjelenítés: a UTC-instant-ot a felhasználó zónájában (alap Europe/Budapest) mutatjuk.
  const timeZone = useTimeZone();
  const fmtTime = (iso: string): string => formatDateTime(iso, i18n.language, timeZone);

  const emDash = <span className="text-muted-foreground">—</span>;

  // Best-practice audit-sorrend (szereplő-first, mint a legacy/GitHub): KI → MIT → MIN → MIKOR.
  // Best-practice audit-sorrend (MŰVELET-first, mint a CloudTrail/Stripe): MI → MIN → KI → MIKOR.
  // Tipográfia a /employees mintája szerint: az 1. (anchor) oszlop `itemTitle` (13/500/foreground); a többi
  // oszlop a DataTable-cella alapját ÖRÖKLI (13/muted); a cellán belüli al-sorok `tone="tertiary"" (halvány+light).
  // EXPORT: KEYSET-cursoros lapozás (az eseménynapló nem offset-elhető), az AKTÍV szűrőkkel. Az esemény-tömeg
  // itt a legnagyobb → a `collectPages` 10 000 soros plafonja ELÉRHETŐ, és ilyenkor a hook figyelmeztet
  // (a szűkítést — dátum-tartomány, kategória — a felhasználó dönti el).
  const fetchExportPage = useCallback(
    async (cursor: string | undefined) => {
      const r = await api.activityLog.query({
        query: {
          q: q || undefined,
          from: from || undefined,
          to: to || undefined,
          category,
          app: appFilter || undefined,
          limit: 200,
          ...(cursor ? { cursor } : {}),
        },
      });
      if (r.status !== 200) return null;
      return { rows: r.body.events, next: r.body.nextCursor };
    },
    [q, from, to, category, appFilter],
  );

  const columns: DataTableColumn<ActivityEvent>[] = [
    {
      // 1. Művelet (MI) — ANCHOR: ember-olvasható címke (itemTitle) + siker/hiba-badge + kinyíló detail
      //    (mezőszintű diff + IP/eszköz).
      id: 'action',
      header: t('activityLog.columns.action'),
      cardLabel: t('activityLog.columns.action'),
      width: 'minmax(0,2.2fr)',
      multiline: true,
      // A művelet OLVASHATÓ címkéje + a kimenet (siker/hiba) — a nyers `action` kulcs (pl.
      // `clients.update_client`) nem audit-olvasmány. A mezőszintű diff NEM megy az exportba: az egy
      // több-soros, strukturált panel, ami egy táblázat-cellában olvashatatlan (és az audit-forrás Odin-oldalon
      // teljes egészében megvan).
      exportValue: (e) =>
        [
          actionLabel(e.action),
          // A kimenetet a UI is CSAK a hiba-esetben jelzi (badge/tooltip: „sikertelen") — a siker az alap,
          // nincs rá címke. Az export ezt tükrözi: siker → nincs toldalék.
          e.status === 'failed' ? t('activityLog.statusLabel.failed') : '',
        ]
          .filter(Boolean)
          .join(' · '),
      cell: (e) => <ActionCell event={e} label={actionLabel(e.action)} />,
      // MOBIL: a fejlécbe CSAK a cím (a kártya „Részletek" gombja a kapcsoló), a diff/hiba/meta panel pedig a
      // „Részletek" blokkba. Enélkül két azonos feliratú kibontó állt egymás mellett a kártyán.
      cardCell: (e) => <ActionTitle event={e} label={actionLabel(e.action)} />,
      cardExtra: (e) => <ActionDetail event={e} />,
    },
    {
      // 2. Érintett entitás (MIN) — OLVASHATÓ (sosem nyers ref/UUID): admin → szerepkör + érintett személy;
      //    natív → entitás-típus + ügyfél/fiók; legacy → ügyfél/fiók + tárgykör. Primary: örökölt 13/muted;
      //    al-sorok: tertiary.
      id: 'target',
      header: t('activityLog.columns.target'),
      cardLabel: t('activityLog.columns.target'),
      card: 'subtitle',
      width: 'minmax(0,1.6fr)',
      multiline: true,
      exportValue: (e) => {
        const lines = targetLines(e);
        // Az export ugyanazt mondja, mint a felület: a típus is bekerül (zárójelben), ha van.
        const type = refEntityType(e.resourceRef);
        const typeLabel = type
          ? t(`activityLog.entity.${type}`, { defaultValue: '' }).toLocaleLowerCase(i18n.language)
          : '';
        const base = lines ? [lines.primary, lines.secondary].filter(Boolean).join(' — ') : '';
        if (!typeLabel) return base;
        return base ? `${base} (${typeLabel})` : typeLabel;
      },
      cell: (e) => {
        const lines = targetLines(e);
        const badge = entityBadge(e.resourceRef);
        if (!lines) return badge ?? emDash;
        return (
          <div className="min-w-0">
            <div className="truncate">{lines.primary}</div>
            {lines.secondary && (
              <Text tone="tertiary" as="div" className="truncate">
                {lines.secondary}
              </Text>
            )}
            {badge && <div className="mt-0.5">{badge}</div>}
          </div>
        );
      },
    },
    {
      // 3. Szereplő (KI) — avatar (belső: valós szines profilkép + részleg-szín) + név (örökölt 13/muted) +
      //    e-mail (al-sor: tertiary). NEM anchor → a név nem itemTitle; az avatar adja a vizuális azonosítást.
      id: 'actor',
      header: t('activityLog.columns.actor'),
      cardLabel: t('activityLog.columns.actor'),
      card: 'subtitle',
      width: 'minmax(0,1.5fr)',
      multiline: true,
      exportValue: (e) =>
        [
          e.actorId || e.actorName || e.actorEmail
            ? formatName(
                {
                  givenName: e.actorGivenName,
                  familyName: e.actorFamilyName,
                  displayName: e.actorName ?? e.actorEmail,
                },
                i18n.language,
              )
            : t('activityLog.system'),
          e.actorEmail,
        ]
          .filter(Boolean)
          .join('\n'),
      cell: (e) => {
        // Locale-tudatos névsor (hu → „Vezetéknév Keresztnév"), mint a Munkatársak/Kapcsolattartók/Jogosultságok
        // lapokon; rendszer-eseménynél (nincs aktor) „Rendszer".
        const name =
          e.actorId || e.actorName || e.actorEmail
            ? formatName(
                {
                  givenName: e.actorGivenName,
                  familyName: e.actorFamilyName,
                  displayName: e.actorName ?? e.actorEmail,
                },
                i18n.language,
              )
            : t('activityLog.system');
        return (
          <PersonCell
            // MEGSZEMÉLYESÍTÉS: a napló az aktort a VALÓDI aktorral írja — a badge mondja meg, hogy a művelet
            // valaki más nevében történt (enélkül a sor a támogató saját műveletének látszana).
            badge={
              e.impersonatedId ? (
                <DomainBadge
                  kind="marker"
                  value="impersonated"
                  label={t('activityLog.impersonatedBadge', {
                    // A NÉVSORREND nyelv-függő (magyarul vezetéknév elöl): a `impersonatedName` az Odin
                    // `display_name`-je, ami a migrált adatban „keresztnév vezetéknév" alakú — magyar
                    // felületen fordítva jelent meg (user-lelet). A részekből a `formatName`
                    // rendel sorrendet, és a `displayName` a fallback (törölt/régi identitáson csak az van).
                    name: formatName(
                      {
                        givenName: e.impersonatedGivenName,
                        familyName: e.impersonatedFamilyName,
                        displayName: e.impersonatedName ?? t('activityLog.impersonatedUnknown'),
                      },
                      i18n.language,
                    ),
                  })}
                />
              ) : null
            }
            // A NÉV már feloldott (rendszer-eseménynél „Rendszer") → `name`-ként adjuk, strukturált részek nélkül.
            // `nameVariant="inherit"`: a napló design-ja RÖGZÍTETT — a szereplő-név a cella tipográfiáját
            // tartja (itt a művelet a sor azonosítója, nem a szereplő).
            nameVariant="inherit"
            name={name}
            avatarUrl={e.actorAvatarUrl}
            accentColor={e.actorTeamColor}
            // Az e-mail csak akkor másodlagos sor, ha VAN aktor (rendszer-eseménynél nincs mit mutatni).
            secondary={e.actorName && e.actorEmail ? e.actorEmail : null}
          />
        );
      },
    },
    {
      // 4. Időpont (MIKOR) — occurredAt, a UI nyelvének megfelelő formátumban (örökölt muted, tabuláris számok).
      id: 'time',
      header: t('activityLog.columns.time'),
      cardLabel: t('activityLog.columns.time'),
      card: 'trailing',
      width: 'minmax(0,1fr)',
      className: 'tabular-nums',
      // TIPIZÁLT export-cella: a NYERS ISO-instant megy át, és a formátum dönti el az alakját — a `.xlsx`
      // valódi dátum-idő cellát kap (rendezhető, szűrhető, a felhasználó Excel-beállítása szerint jelenik
      // meg), a CSV/JSON pedig ISO-t (gépi feldolgozásra). Korábban a LÁTOTT, lokalizált szöveg ment ki:
      // olvasható volt, de Excelben szövegként rendeződött (2026. 10. < 2026. 9.) és nem szűrhető.
      exportType: 'datetime',
      exportValue: (e) => e.occurredAt,
      cell: (e) => <span className="truncate">{fmtTime(e.occurredAt)}</span>,
    },
  ];

  const exportCtl = useListExport<ActivityEvent, string>({
    columns,
    slug: 'activity-log',
    fetchPage: fetchExportPage,
    filters: { q, from, to, category, app: appFilter },
  });

  return (
    <AppShell
      secondaryActions={[
        // MOBIL: a sáv export-gombja el van rejtve (`hidden md:block`) — a FAB-ban viszont elérhető marad.
        // FORMÁTUM-VÁLASZTÁS NÉLKÜL, `xlsx`-re: a FAB egy koppintásos felület, egy legördülő a legördülőben
        // zsákutca lenne; az `xlsx` a leggyakoribb cél (a deszktop-gomb továbbra is mind a hármat kínálja).
        {
          key: 'export',
          icon: <ArrowDownTrayIcon />,
          label: t('common.export.label'),
          onClick: () => exportCtl.run('xlsx'),
        },
      ]}
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            // Egységes placeholder minden listaoldalon: common „Keresés…" (megbeszélt konvenció).
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            // App-szűrő (KÖZPONTI konzol) — CSAK több appnál; default Atlas, „Minden app" (`''`) a kereszt-app feedhez.
            ...(showSelector
              ? [
                  {
                    key: 'app',
                    label: t('activityLog.appLabel'),
                    value: appFilter,
                    onChange: (v: string) => setFilters({ app: v }),
                    options: apps.map((a) => ({ value: a.key, label: a.name })),
                    allValue: '',
                    // APP-nevek (Atlas, Hub, …) → nem kisbetűsítjük.
                    preserveCase: true,
                  },
                ]
              : []),
            {
              key: 'view',
              label: t('activityLog.viewLabel'),
              value: view,
              onChange: (v) => setFilters({ view: v }),
              // a „mind" a Minden esemény; az alapnézet is aktív szűrő (Andi #95)
              allValue: 'all',
              options: [
                { value: 'default', label: t('activityLog.view.default') },
                { value: 'data', label: t('activityLog.view.data') },
                { value: 'admin', label: t('activityLog.view.admin') },
                { value: 'auth', label: t('activityLog.view.auth') },
                { value: 'security', label: t('activityLog.view.security') },
                { value: 'system', label: t('activityLog.view.system') },
              ],
            },
          ]}
          dateFilters={[
            {
              key: 'from',
              label: t('activityLog.from'),
              value: from,
              onChange: (v: string) => setFilters({ from: v }),
            },
            {
              key: 'to',
              label: t('activityLog.to'),
              value: to,
              onChange: (v: string) => setFilters({ to: v }),
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('activityLog.loading')
              : !isError && !infinite.isLoading
                ? t('activityLog.count', { count: rows.length })
                : ''
          }
          actions={
            // Ezen az oldalon NINCS „+ Új" gomb (a naplót nem írjuk kézzel) — az export az egyetlen akció.
            <div className="hidden md:block">
              <ListExportButton
                onExport={exportCtl.run}
                pending={exportCtl.pending}
                hasRows={!isError && rows.length > 0}
              />
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<ActivityEvent>
          columns={columns}
          rows={rows}
          getRowId={(e) => e.id}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('activityLog.noMatches')}
                description={t('activityLog.noMatchesDesc')}
              />
            ) : (
              <EmptyState title={t('activityLog.empty')} description={t('activityLog.emptyDesc')} />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      </div>
    </AppShell>
  );
}

/** Van-e a soron kibontható részlet (mezőszintű diff, hiba-szöveg vagy forenzikus meta)? */
function hasActionDetail(event: ActivityEvent): boolean {
  const hasMeta = event.ip !== null || event.userAgent !== null || event.traceId !== null;
  return event.changes.length > 0 || hasMeta || event.status === 'failed';
}

/**
 * A művelet-cella CÍM-része: ember-olvasható címke (itemTitle) + a SIKERTELEN jelölése. Kibontó NÉLKÜL.
 *
 * Külön komponens, mert a MOBIL-KÁRTYÁN ez a `cardCell`: ott a kártya saját „Részletek" gombja a kapcsoló, a
 * cellának tehát nem lehet sajátja (különben két azonos feliratú kibontó áll egymás mellett — a
 * user-jelentette lelet). A részlet-tartalom a `cardExtra`-ba megy → `ActionDetail`.
 */
function ActionTitle({ event, label }: { event: ActivityEvent; label: string }): JSX.Element {
  const { t } = useTranslation();
  const failed = event.status === 'failed';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* 1. (anchor) oszlop → itemTitle (13/500/foreground), mint a listaoldalak első oszlopa. */}
      <Text variant="itemTitle" as="span" className="truncate">
        {label}
      </Text>
      {/* A siker NÉMA (implicit norma); csak a SIKERTELEN ugrik ki: kis piros ikon + tooltip. */}
      {failed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <ExclamationCircleIcon
              className="text-danger-600 size-4 shrink-0"
              aria-label={t('activityLog.statusLabel.failed')}
            />
          </TooltipTrigger>
          <TooltipContent>{t('activityLog.statusLabel.failed')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * A művelet-cella RÉSZLET-panelje: mezőszintű diff + hiba-szöveg + forenzikus meta. Kibontó NÉLKÜL —
 * a hívó dönti el, mikor látszik (desktopon a cella kibontója, mobilon a kártya „Részletek" gombja).
 * A diff a szerver REDAKÁLT `changes` mezőjéből — a nyers before/after SOHA nem jön ki (PII-fegyelem).
 */
function ActionDetail({ event }: { event: ActivityEvent }): JSX.Element | null {
  if (!hasActionDetail(event)) return null;
  const hasChanges = event.changes.length > 0;
  const hasMeta = event.ip !== null || event.userAgent !== null || event.traceId !== null;
  const failed = event.status === 'failed';
  return (
    <div className="space-y-2">
      {hasChanges && (
        <ChangeList changes={event.changes} domain={event.action.split('.')[0] ?? ''} />
      )}
      {failed && event.errorText && (
        <p className="text-danger-600 border-danger-600/40 border-l-2 pl-2.5 text-xs">
          {event.errorText}
        </p>
      )}
      {hasMeta && <MetaLine ip={event.ip} userAgent={event.userAgent} traceId={event.traceId} />}
    </div>
  );
}

/**
 * Művelet-cella a DESKTOP táblához: cím + saját kibontó + a részlet-panel. A mobil-kártya NEM ezt használja
 * (lásd `ActionTitle` / `ActionDetail` + a `cardCell`/`cardExtra` oszlop-propok).
 */
function ActionCell({ event, label }: { event: ActivityEvent; label: string }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasChanges = event.changes.length > 0;
  const hasDetail = hasActionDetail(event);
  const toggleLabel = hasChanges
    ? t('activityLog.changesCount', { count: event.changes.length })
    : t('common.details'); // EGY kulcs a „Részletek" szóra (CC4) — nincs modul-duplikátum
  return (
    <div className="min-w-0 space-y-1">
      <ActionTitle event={event} label={label} />
      {hasDetail && (
        <>
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              setOpen((v) => !v);
            }}
            className="text-subtle hover:text-foreground focus-visible:ring-accent-600 text-md -ml-0.5 flex items-center gap-1 rounded font-light transition-colors focus-visible:outline-none focus-visible:ring-2"
            aria-expanded={open}
          >
            <ChevronRightIcon
              className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')}
            />
            {toggleLabel}
          </button>
          {open && (
            <div className="mt-1.5">
              <ActionDetail event={event} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Forenzikus request-metaadat a detailben: IP-cím + eszköz (a user-agentből olvashatóan) + OTel trace-id. */
function MetaLine({
  ip,
  userAgent,
  traceId,
}: {
  ip: string | null;
  userAgent: string | null;
  traceId: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const device = userAgent ? parseUserAgent(userAgent) : null;
  return (
    <dl className="text-muted-foreground space-y-0.5 border-l-2 border-[color:var(--table-hairline)] pl-2.5 text-xs">
      {ip && (
        <div className="flex gap-1.5">
          <dt className="font-medium">{t('activityLog.ip')}:</dt>
          <dd className="font-mono">{ip}</dd>
        </div>
      )}
      {device && (
        <div className="flex gap-1.5">
          <dt className="font-medium">{t('activityLog.device')}:</dt>
          <dd className="truncate" title={userAgent ?? undefined}>
            {device}
          </dd>
        </div>
      )}
      {traceId && (
        <div className="flex gap-1.5">
          <dt className="font-medium">{t('activityLog.trace')}:</dt>
          <dd className="truncate font-mono" title={traceId}>
            {traceId}
          </dd>
        </div>
      )}
    </dl>
  );
}

/** Könnyűsúlyú user-agent → „Böngésző · OS" (nincs külső függőség; ismeretlen → nyers, rövidítve). A sorrend
 *  számít: az Edge/Opera UA a „Chrome/"-ot is tartalmazza, a Chrome a „Safari/"-t → az elsőt vesszük. */
function parseUserAgent(ua: string): string {
  const BROWSERS: [string, string][] = [
    ['Edg/', 'Edge'],
    ['OPR/', 'Opera'],
    ['Chrome/', 'Chrome'],
    ['Firefox/', 'Firefox'],
    ['Safari/', 'Safari'],
  ];
  const OSES: [string, string][] = [
    ['Windows', 'Windows'],
    ['Mac OS X', 'macOS'],
    ['Macintosh', 'macOS'],
    ['Android', 'Android'],
    ['iPhone', 'iOS'],
    ['iPad', 'iOS'],
    ['Linux', 'Linux'],
  ];
  const browser = BROWSERS.find(([needle]) => ua.includes(needle))?.[1] ?? null;
  const os = OSES.find(([needle]) => ua.includes(needle))?.[1] ?? null;
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os ?? (ua.length > 40 ? `${ua.slice(0, 40)}…` : ua);
}

/** Mezőszintű régi→új diff-lista: lokalizált mezőnév, `− régi` (piros) → `+ új` (zöld); ∅ az üres értékre. */
function ChangeList({
  changes,
  domain,
}: {
  changes: ActivityEvent['changes'];
  domain: string;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  // Időzóna-tudatos DÁTUM a diffben is: a tárolt érték UTC-instant (2026-08-31T07:30:00.000Z), amit nyersen
  // kiírva a felhasználónak fejben kellett zónáznia — épp az IDŐPONT-módosításnál (találkozó), ahol a diff a
  // lényeg. A lista-oszlopok ugyanezt a `formatDateTime`-ot használják → a napló önmagával konzisztens.
  const timeZone = useTimeZone();
  const fieldLabel = (field: string): string =>
    t(`activityLog.field.${field}`, { defaultValue: humanizeField(field) });
  // Az ÉRTÉK is lokalizált: (1) enum → a modul-szótárakból, (2) ISO-dátum/instant → zónázott, olvasható alak,
  // (3) egyébként a nyers érték marad (a napló elsődlegesen hiteles nyom, nem szépség).
  const valueLabel = (field: string, value: unknown): string | null =>
    diffValueLabel(domain, field, value, t) ?? isoDateLabel(value, i18n.language, timeZone);
  return (
    <ul className="mt-1.5 space-y-1.5 border-l-2 border-[color:var(--table-hairline)] pl-2.5">
      {changes.map((c, i) => (
        <li key={`${c.field}-${i}`} className="min-w-0 text-xs">
          <div className="text-foreground font-medium">{fieldLabel(c.field)}</div>
          <div className="flex flex-wrap items-center gap-1.5">
            <DiffValue
              value={c.old}
              label={valueLabel(c.field, c.old)}
              tone="old"
              empty={t('activityLog.emptyValue')}
            />
            <ChevronRightIcon className="text-muted-foreground size-3 shrink-0" />
            <DiffValue
              value={c.new}
              label={valueLabel(c.field, c.new)}
              tone="new"
              empty={t('activityLog.emptyValue')}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Egy diff-érték chip: régi = piros/áthúzott, új = zöld; üres (null/'') → ∅ jel. Objektum/tömb → tömör JSON. */
function DiffValue({
  value,
  label,
  tone,
  empty,
}: {
  value: unknown;
  /** Lokalizált enum-címke, ha van — a nyers érték a `title`-ben marad (a napló nyoma nem tűnik el). */
  label: string | null;
  tone: 'old' | 'new';
  empty: string;
}): JSX.Element {
  const raw = renderValue(value);
  const str = label ?? raw;
  const isEmpty = str === '';
  return (
    <span
      className={cn(
        'inline-block max-w-[16rem] truncate rounded px-1.5 py-0.5 font-mono text-[11px]',
        tone === 'old'
          ? 'bg-danger-soft text-danger-600 line-through'
          : 'bg-success-soft text-success-600',
      )}
      title={isEmpty ? empty : label === null ? raw : `${label} (${raw})`}
    >
      {isEmpty ? empty : str}
    </span>
  );
}

/** ISO-instant (…T…) vagy dátum-only (YYYY-MM-DD) felismerése — a diff-értékek olvasható megjelenítéséhez. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Dátum-értékű diff → olvasható alak. Az INSTANT (`…T…Z`) a felhasználó zónájában (Europe/Budapest), a
 * DÁTUM-ONLY viszont SZÁNDÉKOSAN UTC-ben formázva: az `YYYY-MM-DD` naptári nap (szerződés-dátum, esedékesség),
 * nem időpillanat — zónázva egy negatív offsetnél egy napot visszacsúszna. Nem dátum / érvénytelen → `null`,
 * ilyenkor a hívó a nyers értéket írja ki (sosem találunk ki dátumot).
 */
function isoDateLabel(value: unknown, lang: string, timeZone: string): string | null {
  if (typeof value !== 'string') return null;
  if (ISO_DATE_ONLY.test(value)) {
    return formatDateTime(value, lang, 'UTC', { dateStyle: 'short' }) || null;
  }
  if (!ISO_INSTANT.test(value)) return null;
  return formatDateTime(value, lang, timeZone) || null;
}

/** Érték → megjelenítendő string (skalár közvetlen; objektum/tömb tömör JSON; null/undefined → ''). */
function renderValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (typeof v === 'number') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return '[…]';
  }
}

/** Mezőnév humanizálás fallback: snake/camel → szóköz, első betű nagy. */
function humanizeField(field: string): string {
  const s = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._]+/g, ' ')
    .trim()
    .toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
